import React, { useState, useEffect, useRef, Fragment } from "react";
import { Link } from "react-router-dom";
import {
  Row,
  Col,
  Form,
  FloatingLabel,
  Card,
  Button,
  Spinner,
  Badge,
  Modal,
  Dropdown,
} from "react-bootstrap";
import { Trash2, Edit3, Plus, Upload, MoreVertical } from "react-feather";

import DataTable from "../../components/table";
import { useNotification } from "../../components/notification";
import { useConfirmation } from "../../contexts/confirmationContext";
import apiService from "../../services/apiService";
import { formatAmount } from "../../utils";
import { useAuth } from "../../contexts/authContext";

const Products = () => {
  const didFetch = useRef(false);
  const { showNotification } = useNotification();
  const { showConfirmation } = useConfirmation();
  const { user } = useAuth();

  const [manufacturers, setManufacturers] = useState([]);
  const [products, setProducts] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingState, setLoadingState] = useState({ action: "", id: "" });

  // Consolidated filter state
  const [filters, setFilters] = useState({
    categoryId: "",
    manufacturer: "",
    sortBy: "",
    sortOrder: "",
    isActive: "",
    page: { PageNumber: 1, PageSize: 10 },
    search: "",
  });

  // State for import modals
  const [showImportProductsModal, setShowImportProductsModal] = useState(false);
  const [showImportFormulationsModal, setShowImportFormulationsModal] =
    useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    if (didFetch.current) return; // Prevent second call
    didFetch.current = true;

    const getCategory = async () => {
      try {
        setIsLoading(true);
        const response = await apiService.get(
          "/category/get-car-search-filter"
        );
        setFilters((prev) => ({
          ...prev,
          categoryId: response.categories[0].id,
        }));
        setManufacturers(response.manufacturers);
      } catch (error) {
        console.error("Error fetching car models:", error);
      } finally {
        setIsLoading(false);
      }
    };
    getCategory();
  }, []);

  // Fetch products when filters change
  useEffect(() => {
    const fetchProducts = async () => {
      setIsLoading(true);
      try {
        const response = await apiService.get(`/products`, {
          page: filters.page.PageNumber,
          limit: filters.page.PageSize,
          categoryId: filters.categoryId,
          manufacturer: filters.manufacturer,
          sortBy: filters.sortBy,
          sortOrder: filters.sortOrder,
          isActive: filters.isActive,
          search: filters.search,
        });
        setProducts(response.products); // Update products data
        setTotalRecords(response.pagination.total); // Update total records
      } catch (error) {
        showNotification("danger", error || "Failed to get products.", 5000);
      } finally {
        setIsLoading(false);
      }
    };
    if (filters.categoryId) fetchProducts();
  }, [filters, showNotification]);

  // Handle search term
  const handleSearchTerm = (text) => {
    setFilters((prev) => ({
      ...prev,
      search: text,
      page: { ...prev.page, PageNumber: 1 },
    }));
  };

  // Handle page change
  const handlePageChange = (pageNumber, pageSize) => {
    setFilters((prev) => ({
      ...prev,
      page: { PageNumber: pageNumber, PageSize: pageSize },
    }));
  };

  // Handle filter change
  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page: { ...prev.page, PageNumber: 1 },
    }));
  };

  // Handle Delete Product
  const handleDeleteProduct = async (
    productId,
    productName,
    hexCode,
    colourGroup
  ) => {
    showConfirmation({
      title: "Delete Product",
      description: `You're about to delete the product **"${productName}"** (Hex: #${hexCode}, Colour Group: ${colourGroup}). This action cannot be undone. Do you wish to continue?`,
      confirmText: "Delete",
      cancelText: "Cancel",
      variant: "primary",
      onConfirm: async () => {
        setLoadingState({ action: "delete", id: productId });
        try {
          await apiService.delete(`/products/${productId}`);
          handlePageChange(filters.page.PageNumber, filters.page.PageSize);
          showNotification("success", "Product deleted successfully!");
        } catch (error) {
          showNotification("danger", error || "Failed to delete product.");
        } finally {
          setLoadingState(null);
        }
      },
    });
  };

  // Handle file selection for import
  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  // Handle import products
  const handleImportProducts = async () => {
    if (!selectedFile) {
      showNotification("danger", "Please select a file.");
      return;
    }

    setLoadingState({ action: "importProducts" });
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("categoryId", filters.categoryId);

      const response = await apiService.post(
        "/import/car-products",
        formData,
        "multipart"
      );
      showNotification("success", response.message);
      setShowImportProductsModal(false);
      handlePageChange(filters.page.PageNumber, filters.page.PageSize); // Refresh the table
    } catch (error) {
      showNotification("danger", error || "Failed to import products.");
    } finally {
      setLoadingState(null);
    }
  };

  // Handle import formulations
  const handleImportFormulations = async () => {
    if (!selectedFile) {
      showNotification("danger", "Please select a file.");
      return;
    }

    setLoadingState({ action: "importFormulations" });
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("categoryId", filters.categoryId);

      const response = await apiService.post(
        "/import/car-products/update/formulations",
        formData,
        "multipart"
      );
      showNotification("success", response.message);

      setShowImportFormulationsModal(false);
      handlePageChange(filters.page.PageNumber, filters.page.PageSize); // Refresh the table
    } catch (error) {
      showNotification("danger", error || "Failed to import formulations.");
    } finally {
      setLoadingState(null);
    }
  };

  // Define columns
  const columns = [
    { accessorKey: "name", header: "Name" },
    { accessorKey: "manufacturer", header: "Manufacturer" },
    { accessorKey: "colourCode", header: "Colour Code" },
    { accessorKey: "formulaCode", header: "Formula Code" },
    { accessorKey: "colourGroup", header: "Colour Group" },
    {
      accessorKey: "hexCode",
      header: "Hex Code",
      cell: (info) => (
        <div className="d-flex align-items-center">
          <div
            className="product rounded-circle me-1"
            style={{
              backgroundColor: info.getValue(),
            }}
          />
          {info.getValue()}
        </div>
      ),
    },
    {
      accessorKey: "price",
      header: "Price",
      cell: (info) => (
        <Fragment>
          <span className="currency">&#8377;</span>
          {formatAmount(info.getValue() || 0)}
        </Fragment>
      ),
    },
    {
      accessorKey: "isActive",
      header: "Status",
      cell: (info) => (
        <Badge
          className="text-uppercase"
          bg={info.getValue() ? "success" : "danger"}
        >
          {info.getValue() ? "Active" : "Inactive"}
        </Badge>
      ),
    },
    {
      accessorKey: "actions",
      header: "Actions",
      cell: (info) => (
        <div>
          <Link
            title="Edit Product"
            to={`/products/edit/${info.row.original.id}`}
            className="btn btn-primary btn-sm me-1 mb-1"
          >
            <Edit3 size={16} />
          </Link>
          {user?.role === "super_admin" && (
            <Button
              variant="danger"
              size="sm"
              title="Delete Product"
              onClick={() =>
                handleDeleteProduct(
                  info.row.original.id,
                  info.row.original.name,
                  info.row.original.hexCode,
                  info.row.original.colourGroup
                )
              }
              disabled={
                loadingState?.action === "delete" &&
                loadingState.id === info.row.original.id
              }
              className="me-1 mb-1"
            >
              {loadingState?.action === "delete" &&
              loadingState.id === info.row.original.id ? (
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
              ) : (
                <Trash2 size={16} />
              )}
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <Card className="p-4 rounded-4">
      <Card.Body>
        <div className="d-flex align-items-center justify-content-between">
          <div>
            <h1 className="mb-2 fs-5">Manage Products</h1>
            <p className="mb-0 text-light-emphasis fw-light">
              View product details, manage product status, etc.
            </p>
          </div>
          {user?.role === "super_admin" && (
            <div className="d-flex align-items-center">
              <Button
                as={Link}
                to="/products/create"
                variant="primary"
                className="btn-main mb-3 mb-sm-0"
              >
                <Plus size={14} className="me-1 align-middle" />
                Add Product
              </Button>

              <Dropdown align="end">
                <Dropdown.Toggle variant="light" className="border-0">
                  <MoreVertical size={16} />
                </Dropdown.Toggle>
                <Dropdown.Menu className="shadow border-0 fs-medium">
                  <Dropdown.Item
                    onClick={() => setShowImportProductsModal(true)}
                  >
                    <Upload size={16} className="me-2" /> Import Products
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={() => setShowImportFormulationsModal(true)}
                  >
                    <Upload size={16} className="me-2" /> Import Formulations
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          )}
        </div>
        <hr className="my-4" />
        <Row className="my-4">
          <Col xs={12} sm={6} lg={3} className="mb-2">
            <FloatingLabel
              controlId="ddManufacturers"
              label="Sort By Manufacturer"
            >
              <Form.Select
                aria-label="Sort By Manufacturer"
                value={filters.manufacturer}
                onChange={(e) =>
                  handleFilterChange("manufacturer", e.target.value)
                }
              >
                <option value="">--</option>
                {manufacturers.map((manufacturer) => (
                  <option key={manufacturer} value={manufacturer}>
                    {manufacturer}
                  </option>
                ))}
              </Form.Select>
            </FloatingLabel>
          </Col>
          <Col xs={12} sm={6} lg={3} className="mb-2">
            <FloatingLabel controlId="ddFields" label="Sort By Field">
              <Form.Select
                aria-label="Sort By Field"
                value={filters.sortBy}
                onChange={(e) => handleFilterChange("sortBy", e.target.value)}
              >
                <option value="">--</option>
                <option value="name">name</option>
                <option value="colourCode">colourCode</option>
                <option value="formulaCode">formulaCode</option>
                <option value="createdAt">createdAt</option>
                <option value="updatedAt">updatedAt</option>
                <option value="colourGroup">colourGroup</option>
              </Form.Select>
            </FloatingLabel>
          </Col>
          <Col xs={12} sm={6} lg={3} className="mb-2">
            <FloatingLabel controlId="ddSortOrder" label="Sort Order">
              <Form.Select
                aria-label="Sort Order"
                value={filters.sortOrder}
                onChange={(e) =>
                  handleFilterChange("sortOrder", e.target.value)
                }
              >
                <option value="">--</option>
                <option value="ASC">ASC</option>
                <option value="DESC">DESC</option>
              </Form.Select>
            </FloatingLabel>
          </Col>
          <Col xs={12} sm={6} lg={3} className="mb-2">
            <FloatingLabel controlId="ddStatus" label="Sort By Status">
              <Form.Select
                aria-label="Sort By Status"
                value={filters.isActive}
                onChange={(e) => handleFilterChange("isActive", e.target.value)}
              >
                <option value="">--</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </Form.Select>
            </FloatingLabel>
          </Col>
        </Row>
        <DataTable
          columns={columns}
          data={products}
          totalRecords={totalRecords}
          page={filters.page}
          onPageChange={handlePageChange}
          searchRecords={handleSearchTerm}
          loadingState={isLoading}
          searchPlaceholder="Search products..."
        />
      </Card.Body>

      {/* Import Products Modal */}
      <Modal
        show={showImportProductsModal}
        onHide={() => setShowImportProductsModal(false)}
        contentClassName="p-4"
        centered
      >
        <Modal.Body>
          <h5>Import Products</h5>
          <p className="mb-4 text-muted">
            Upload an <strong>XLSX</strong> file to import products in bulk.
            Ensure the file follows the required format, including product
            details such as OEM, Colour Code, Formula Code, etc. This action
            will add or update products in the system.
          </p>
          <Form>
            <Form.Group controlId="formFile" className="mb-3">
              <Form.Label>Choose Excel File</Form.Label>
              <Form.Control
                type="file"
                accept=".xlsx, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={handleFileChange}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="danger"
            className="btn-main"
            onClick={() => setShowImportProductsModal(false)}
          >
            Close
          </Button>
          <Button
            variant="primary"
            className="btn-main"
            onClick={handleImportProducts}
            disabled={
              loadingState?.action === "importProducts" || !filters.categoryId
            }
          >
            {loadingState?.action === "importProducts" ? (
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            ) : (
              "Import"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Import Formulations Modal */}
      <Modal
        show={showImportFormulationsModal}
        onHide={() => setShowImportFormulationsModal(false)}
        contentClassName="p-4"
        centered
      >
        <Modal.Body>
          <h5>Import Formulations</h5>
          <p className="mb-4 text-muted">
            Upload an <strong>XLSX</strong> file to import formulations in bulk.
            Ensure the file follows the required format, including product
            details such as OEM, Colour Code, Formula Code, Tinters etc. This
            action will add or update products in the system.
          </p>
          <Form>
            <Form.Group controlId="formFile" className="mb-3">
              <Form.Label>Choose Excel File</Form.Label>
              <Form.Control
                type="file"
                accept=".xlsx, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={handleFileChange}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="danger"
            className="btn-main"
            onClick={() => setShowImportFormulationsModal(false)}
          >
            Close
          </Button>
          <Button
            variant="primary"
            className="btn-main"
            disabled={
              loadingState?.action === "importFormulations" ||
              !filters.categoryId
            }
            onClick={handleImportFormulations}
          >
            {loadingState?.action === "importFormulations" ? (
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            ) : (
              "Import"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
};

export default Products;
